import React, { useEffect, useState, useMemo, Fragment } from 'react'
import injectStyles from 'react-jss'
import moment from 'moment-timezone'
import {
  Fab,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import MoreVertIcon from '@material-ui/icons/MoreVert'

import { useEditSequenceContext } from '../../../context/EditSequenceContext'

import { styles } from './styles'
import DateRangeSelector from '../../../../../../../../../../../Components/DateRangeSelector'
import { TimeInput } from '../../../../../../../../../../../Components/Form'
import { castMomentToTime, castToMoment } from '../../../../../../utils'

const Schedule = ({
  id,
  index,
  cityTimezone,
  classes,
  onDelete,
  handleDrawerToggle,
  isAnyElementBelow,
}) => {
  const {
    sequence,
    deleteSchedule,
    editSequenceContextState,
    updateSchedule,
    setErrors,
  } = useEditSequenceContext()
  const schedule = sequence.DateSequenceTemplate[index]
  const { errors } = editSequenceContextState
  const [anchorEl, setAnchorEl] = useState(null)
  const [startDate, setStartDate] = useState('')
  const [startHours, setStartHours] = useState('')
  const [startMinutes, setStartMinutes] = useState('')
  const [selectedTimeOption, setSelectedTimeOption] = useState('')
  const ITEM_HEIGHT = 48
  const open = useMemo(() => Boolean(anchorEl), [anchorEl])

  const popupOptions = [
    {
      id: 1,
      label: 'Delete',
      func: id => {
        handleClose()
        if (onDelete) {
          onDelete(id)
        } else {
          deleteSchedule(id)
        }
      },
    },
  ]

  const handleChangeStartDate = value => {
    const date = value ? moment(value) : null
    const newStartDate = date ? date.format('YYYY-MM-DD HH:mm:ss') : startDate

    setStartDate(newStartDate)
    const formattedDate = castToMoment({
      year: date.year(),
      month: date.month() + 1,
      day: date.date(),
      hour: startHours || '00',
      minute: startMinutes || '00',
      option: selectedTimeOption,
    }).format('YYYY-MM-DD HH:mm:ss')
    updateSchedule({ ...schedule, start_date: formattedDate })
  }

  const handleChangeStartTime = ({ hours, minutes, selectedOption }) => {
    setStartHours(hours)
    setStartMinutes(minutes)
    setSelectedTimeOption(selectedOption)
    
    let formattedDate = null
    if (startDate) {
      const date = moment(startDate)
      formattedDate = castToMoment({
        year: date.year(),
        month: date.month() + 1,
        day: date.date(),
        hour: hours,
        minute: minutes,
        option: selectedOption,
      }).format('YYYY-MM-DD HH:mm:ss')
    }

    updateSchedule({
      ...schedule,
      start_date: formattedDate,
    })
  }

  const handleClick = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  // Effects
  useEffect(() => {
    const scheduleStartDate = schedule.start_date
      ? moment(schedule.start_date)
      : null
    const scheduleTime = castMomentToTime(scheduleStartDate)

    setStartDate(
      scheduleStartDate ? scheduleStartDate.format('YYYY-MM-DD HH:mm:ss') : '',
    )
    setStartHours(
      scheduleStartDate && scheduleTime.hour ? scheduleTime.hour : '',
    )
    setStartMinutes(
      scheduleStartDate && scheduleTime.minute ? scheduleTime.minute : '',
    )
    setSelectedTimeOption(scheduleTime.option)
  }, [])

  useEffect(() => {
    const date = startDate ? moment(startDate) : null
    if (!startDate || !startHours || !startMinutes) {
      setErrors({
        type: 'schedule',
        error: 'Please provide the complete start date time',
      })
    } else if (
      castToMoment({
        year: date.year(),
        month: date.month() + 1,
        day: date.date(),
        hour: startHours,
        minute: startMinutes,
        option: selectedTimeOption,
      }).format('YYYY-MM-DD HH:mm:ss') <
      moment()
        .tz(cityTimezone)
        .format('YYYY-MM-DD HH:mm:ss')
    ) {
      setErrors({
        type: 'schedule',
        error: 'Cannot set a start date in the past',
      })
    } else {
      setErrors({ type: 'schedule', error: '' })
    }
  }, [startDate, startHours, startMinutes, selectedTimeOption])

  return (
    <Fragment>
      <Grid container direction="column" alignItems="center">
        {index > 0 ? (
          <div>
            <Grid item xs>
              <div className={classes.line} />
            </Grid>
          </div>
        ) : null}
        <Grid item>
          <div className={classes.header}>
            {'Schedule'}
            <div>
              <IconButton onClick={handleClick}>
                <MoreVertIcon style={{ fill: 'white' }} />
              </IconButton>
              <Menu
                id="long-menu"
                anchorEl={anchorEl}
                keepMounted
                open={open}
                onClose={handleClose}
                PaperProps={{
                  style: {
                    maxHeight: ITEM_HEIGHT * 4.5,
                    width: '11ch',
                  },
                }}
              >
                {popupOptions.map(item => (
                  <MenuItem onClick={() => item.func(id)}>
                    <div className={classes.popupItem}>{item.label}</div>
                  </MenuItem>
                ))}
              </Menu>
            </div>
          </div>
        </Grid>
        <Grid item>
          <div className={classes.body}>
            <Grid container direction="column">
              <div className={classes.wrapperSelect}>
                <DateRangeSelector
                  onChange={handleChangeStartDate}
                  title="Start date"
                  value={startDate}
                  icon="schedule"
                />
                <TimeInput
                  className={classes.timePicker}
                  hours={startHours}
                  minutes={startMinutes}
                  selectedOption={selectedTimeOption}
                  onChange={handleChangeStartTime}
                  title="Time"
                />
              </div>

              {Boolean(errors.schedule) ? (
                <Typography
                  className={classes.errorMessage}
                  color="error"
                  variant="caption"
                >
                  {errors.schedule}
                </Typography>
              ) : null}
            </Grid>
          </div>
        </Grid>
        <Grid item xs>
          <div className={classes.line} />
        </Grid>
        {!isAnyElementBelow ? (
          <Grid item xs>
            <Fab
              color="primary"
              aria-label="add"
              onClick={() => handleDrawerToggle(true)}
            >
              <AddIcon />
            </Fab>
          </Grid>
        ) : null}
      </Grid>
    </Fragment>
  )
}

export default injectStyles(styles)(Schedule)
